import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { ChevronDown, Check } from "lucide-react";
import "./Dropdown.css";
import { computePosition, flip, shift, size, offset, autoUpdate } from '@floating-ui/dom';

export default function Dropdown({
  options = [],
  value = null,
  onChange,
  placeholder = "Select…",
  disabled = false,
  ariaLabel,
  className = "",
  maxVisible = 7,
  invalid = false,
}) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [menuPos, setMenuPos] = useState({ left: 0, top: 0, width: 0, maxH: 280, origin: 'top', placement: 'bottom-start' });
  const optionsKey = useMemo(() => options.map(o => o.value).join("|"), [options]);
  const btnRef = useRef(null);
  const listRef = useRef(null);   // <-- portal menu element
  const rootRef = useRef(null);   // trigger wrapper
   const rowHeight = 52;
  const desiredMax = Math.max(1, maxVisible) * rowHeight;

  const selectedOption = useMemo(
    () => options.find((o) => o.value === value) || null,
    [options, value]
  );

  // Close on outside click BUT ignore clicks inside the portal menu
  useEffect(() => {
    if (!open) return;

    const onDocPointerDown = (e) => {
      const t = e.target;
      if (rootRef.current?.contains(t)) return;      // click on trigger area
      if (listRef.current?.contains(t)) return;      // click inside portal menu -> do NOT close
      setOpen(false);
    };

    document.addEventListener("pointerdown", onDocPointerDown, true);
    return () => document.removeEventListener("pointerdown", onDocPointerDown, true);
  }, [open]);

  // Positioning



  async function placeMenu() {
      const reference = btnRef.current;
      const floating  = listRef.current;
      if (!reference || !floating) return;
   
      // match trigger width
      const width = reference.getBoundingClientRect().width;
      floating.style.width = `${Math.round(width)}px`;
      floating.style.position = 'fixed'; // keep using portal fixed
   
      const { x, y, placement } = await computePosition(reference, floating, {
        strategy: 'fixed',
        placement: 'bottom-start',
        middleware: [
          offset(8),
          flip({ fallbackPlacements: ['top-start', 'bottom-end', 'top-end'] }),
          shift({ padding: 8 }),
          size({
            apply({ availableHeight, elements }) {
              elements.floating.style.maxHeight = `${Math.min(availableHeight, desiredMax)}px`;
              elements.floating.style.overflow = 'auto';
            }
          }),
        ],
      });
   
      setMenuPos({
        left: Math.round(x),
        top: Math.round(y),
        width: Math.round(width),
        maxH: desiredMax,
        origin: placement.startsWith('top') ? 'bottom' : 'top',
        placement,
      });
    }
 // Reposition on open/scroll/resize
 useEffect(() => {
  if (!open) return;
    placeMenu();
    const stop = autoUpdate(btnRef.current, listRef.current, placeMenu, { animationFrame: true });
    const curIdx = Math.max(0, options.findIndex(o => o.value === value));
    setActiveIndex(curIdx);
    // focusing after first compute avoids iOS scroll quirks
    requestAnimationFrame(() => listRef.current?.focus());
    return () => stop();
},[open, optionsKey]);

  // Keyboard
  const moveActive = (dir) => {
    if (!options.length) return;
    let next = activeIndex;
    if (dir === "down") next = (activeIndex + 1 + options.length) % options.length;
    if (dir === "up") next = (activeIndex - 1 + options.length) % options.length;
    setActiveIndex(next);
    const item = listRef.current?.querySelector(`[data-idx="${next}"]`);
    item?.scrollIntoView({ block: "nearest" });
  };

  const commitSelection = (idx) => {
    const opt = options[idx];
    if (!opt) return;
    onChange?.(opt.value);
    setOpen(false);
  };

  const onButtonKeyDown = (e) => {
    if (disabled) return;
    if ([" ", "Enter", "ArrowDown", "ArrowUp"].includes(e.key)) {
      e.preventDefault();
      if (!open) {
        setOpen(true);
      } else {
        if (e.key === "ArrowDown") moveActive("down");
        if (e.key === "ArrowUp") moveActive("up");
        if (e.key === "Enter") commitSelection(activeIndex);
      }
    }
  };

  const onListKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      btnRef.current?.focus();
    } else if (e.key === "ArrowDown") {
      e.preventDefault(); moveActive("down");
    } else if (e.key === "ArrowUp") {
      e.preventDefault(); moveActive("up");
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault(); commitSelection(activeIndex);
    } else if (e.key === "Tab") {
      setOpen(false);
    }
  };

  const menu = (
    <div
      className={`pm-dd__menu pm-dd__menu--portal pm-dd-portal ${open ? "is-open" : ""}`}
      role="listbox"
      tabIndex={-1}
      ref={listRef}
      onKeyDown={onListKeyDown}
      style={{
        left: `${menuPos.left}px`,
        top: `${menuPos.top}px`,
        width: `${menuPos.width}px`,
        maxHeight: `${menuPos.maxH}px`,
        transformOrigin: menuPos.origin,
        zIndex: 2147483647
      }}
    >
      {options.length === 0 && <div className="pm-dd__empty">No options</div>}
      {options.map((opt, idx) => {
  const selected = opt.value === value;
  const active = idx === activeIndex;

  // Usage inside map
  const isCustom = opt.value === "custom"; 
  return (
    <div
      key={opt.value}
      role="option"
      aria-selected={selected}
      className={`pm-dd__option ${selected ? "is-selected" : ""} ${active ? "is-active" : ""}`}
      data-idx={idx}
      onMouseEnter={() => setActiveIndex(idx)}
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => commitSelection(idx)}
    >
<div
  className="pm-dd__text"
  style={{
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    minWidth: 0,
  }}
>
  <div
    style={{
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 10,
      minWidth: 0,
    }}
  >
    <span className="pm-dd__label">{opt.label}</span>
    {isCustom && (
      <span
        style={{
          marginLeft: 10,
          padding: "6px 10px",
          borderRadius: "var(--radius)",
          fontSize: 12,
          fontWeight: 600,
          color: "var(--green-font)",
          background: "var(--light-green-bg)",
          whiteSpace: "nowrap",
          letterSpacing: 0.2,
        }}
      >
        Customizable
      </span>
    )}
  </div>
  {opt.help && (
    <span className="pm-dd__help" style={{ marginTop: 2 }}>
      {opt.help}
    </span>
  )}
</div>
{selected && <Check className="pm-dd__check" aria-hidden="true" />}

    </div>
  );
})}

    </div>
  );

  return (
    <div
      ref={rootRef}
      className={`pm-dd ${disabled ? "pm-dd--disabled" : ""} ${invalid ? "is-invalid" : ""} ${className}`}
      data-open={open ? "true" : "false"}
    >
      <button
        ref={btnRef}
        type="button"
        className="pm-dd__button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-invalid={invalid ? "true" : "false"}
        aria-label={ariaLabel || placeholder}
        onClick={() => !disabled && setOpen((o) => !o)}
        onKeyDown={onButtonKeyDown}
        disabled={disabled}
      >
        <span className={`pm-dd__value ${selectedOption ? "has-value" : ""}`}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ChevronDown className="pm-dd__chevron" aria-hidden="true" />
      </button>

      {ReactDOM.createPortal(menu, document.body)}
    </div>
  );
}
